import React, { useEffect, useMemo, useState } from "react";
import { RefreshCw, Search, Inbox, Clock, PhoneCall, CheckCircle2, XCircle, Mail, MailOpen } from "lucide-react";
import { supabase } from "../../supabaseClient";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";

// Status possíveis de uma solicitação — devem espelhar o CHECK da migration
// 20260721000001_create_solicitacoes_familia.sql.
const STATUS = {
  aguardando: { label: "Aguardando", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  em_contato: { label: "Em contato", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  concluida: { label: "Concluída", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelada: { label: "Cancelada", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const STATUS_KEYS = Object.keys(STATUS);

const nomeCompleto = (u) =>
  u ? `${u.nome ?? ""} ${u.sobrenome ?? ""}`.trim() || "—" : "—";

const formatarDataHora = (iso) =>
  iso
    ? new Date(iso).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const MetricCard = ({ titulo, valor, icone, className }) => (
  <Card className={className}>
    <CardContent className="flex items-center gap-3 py-4">
      <div className="text-indigo-400">{icone}</div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground truncate">{titulo}</p>
        <p className="text-lg font-bold text-foreground">{valor}</p>
      </div>
    </CardContent>
  </Card>
);

// ─── Contratações (solicitacoes_familia) ─────────────────────────────────────

const SolicitacaoRow = ({ solicitacao, onStatusChange, saving }) => {
  const cfg = STATUS[solicitacao.status] || STATUS.aguardando;
  return (
    <div
      id={`admin-solicitacao-${solicitacao.id}`}
      className="flex flex-col gap-3 rounded-xl border border-warm-100 bg-white p-3 lg:flex-row lg:items-center"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate">
          {nomeCompleto(solicitacao.familia)}
          <span className="text-muted-foreground font-normal"> → </span>
          {nomeCompleto(solicitacao.baba)}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {solicitacao.familia?.email || "—"} · {formatarDataHora(solicitacao.criado_em)}
        </p>
        {solicitacao.mensagem && (
          <p className="mt-1 text-xs text-warm-600 line-clamp-2">
            “{solicitacao.mensagem}”
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <Badge variant="outline" className={cfg.cls}>{cfg.label}</Badge>
        <Select
          value={solicitacao.status}
          onValueChange={(v) => onStatusChange(solicitacao.id, v)}
          disabled={saving}
        >
          <SelectTrigger className="w-40" id={`admin-solicitacao-status-${solicitacao.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_KEYS.map((k) => (
              <SelectItem key={k} value={k}>{STATUS[k].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

const ContratacoesTab = () => {
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [busca, setBusca] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("todos");
  const [savingId, setSavingId] = useState(null);

  const fetchSolicitacoes = async () => {
    setLoading(true);
    setErro(null);
    try {
      const { data, error } = await supabase
        .from("solicitacoes_familia")
        .select(
          "*, familia:usuarios!familia_id(nome, sobrenome, email), baba:usuarios!baba_id(nome, sobrenome)",
        )
        .order("criado_em", { ascending: false });
      if (error) throw error;
      setSolicitacoes(data || []);
    } catch (e) {
      console.error("Erro ao buscar solicitações:", e);
      setErro(e.message || "Falha ao carregar solicitações.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSolicitacoes();
  }, []);

  const atualizarStatus = async (id, novoStatus) => {
    setSavingId(id);
    const anterior = solicitacoes.find((s) => s.id === id)?.status;
    // Otimista
    setSolicitacoes((prev) => prev.map((s) => (s.id === id ? { ...s, status: novoStatus } : s)));
    const { error } = await supabase
      .from("solicitacoes_familia")
      .update({ status: novoStatus })
      .eq("id", id);
    if (error) {
      console.error("Erro ao atualizar status:", error);
      setSolicitacoes((prev) => prev.map((s) => (s.id === id ? { ...s, status: anterior } : s)));
      alert("Não foi possível atualizar o status.");
    }
    setSavingId(null);
  };

  const metricas = useMemo(() => {
    const c = { aguardando: 0, em_contato: 0, concluida: 0, cancelada: 0 };
    solicitacoes.forEach((s) => {
      if (c[s.status] != null) c[s.status] += 1;
    });
    return c;
  }, [solicitacoes]);

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return solicitacoes.filter((s) => {
      const casaStatus = statusFiltro === "todos" || s.status === statusFiltro;
      if (!termo) return casaStatus;
      const alvo = `${nomeCompleto(s.familia)} ${nomeCompleto(s.baba)} ${s.familia?.email ?? ""}`.toLowerCase();
      return casaStatus && alvo.includes(termo);
    });
  }, [solicitacoes, busca, statusFiltro]);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard titulo="Aguardando" valor={metricas.aguardando} icone={<Clock size={22} />} className="bg-amber-50 border-amber-100" />
        <MetricCard titulo="Em contato" valor={metricas.em_contato} icone={<PhoneCall size={22} />} className="bg-blue-50 border-blue-100" />
        <MetricCard titulo="Concluídas" valor={metricas.concluida} icone={<CheckCircle2 size={22} />} className="bg-emerald-50 border-emerald-100" />
        <MetricCard titulo="Canceladas" valor={metricas.cancelada} icone={<XCircle size={22} />} className="bg-slate-50 border-slate-200" />
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="admin-solicitacoes-busca"
            placeholder="Buscar por família, babá ou e-mail..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFiltro} onValueChange={setStatusFiltro}>
          <SelectTrigger className="sm:w-48" id="admin-solicitacoes-filtro">
            <SelectValue placeholder="Todos os status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os status</SelectItem>
            {STATUS_KEYS.map((k) => (
              <SelectItem key={k} value={k}>{STATUS[k].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchSolicitacoes} disabled={loading}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Recarregar
        </Button>
      </div>

      {erro && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-4 text-sm text-red-700">
            <p className="font-semibold">Não foi possível carregar as solicitações.</p>
            <p className="text-xs mt-1">{erro}</p>
          </CardContent>
        </Card>
      )}

      {loading && !erro && (
        <Card className="border-dashed">
          <CardContent className="py-6 text-sm text-muted-foreground">Carregando solicitações...</CardContent>
        </Card>
      )}

      {!loading && !erro && filtradas.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-6 text-sm text-muted-foreground">
            {solicitacoes.length === 0
              ? "Nenhuma solicitação de contratação até agora."
              : "Nenhuma solicitação corresponde ao filtro."}
          </CardContent>
        </Card>
      )}

      {!loading && !erro && filtradas.length > 0 && (
        <div className="space-y-2">
          {filtradas.map((s) => (
            <SolicitacaoRow key={s.id} solicitacao={s} onStatusChange={atualizarStatus} saving={savingId === s.id} />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Suporte (mensagens_suporte) ─────────────────────────────────────────────

const MensagemRow = ({ mensagem, onToggleLida, saving }) => (
  <div
    id={`admin-mensagem-${mensagem.id}`}
    className={`flex flex-col gap-2 rounded-xl border p-3 sm:flex-row sm:items-start ${
      mensagem.lida ? "border-warm-100 bg-white" : "border-indigo-200 bg-indigo-50/40"
    }`}
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className="text-sm font-semibold text-foreground truncate">
          {nomeCompleto(mensagem.familia)}
        </p>
        {!mensagem.lida && <Badge variant="outline" className="bg-indigo-100 text-indigo-700 border-indigo-200">Nova</Badge>}
      </div>
      <p className="text-xs text-muted-foreground truncate">
        {mensagem.familia?.email || "—"} · {formatarDataHora(mensagem.criado_em)}
      </p>
      {mensagem.assunto && (
        <p className="mt-1 text-xs font-medium text-warm-700">{mensagem.assunto}</p>
      )}
      <p className="mt-1 text-sm text-warm-600 whitespace-pre-line">{mensagem.mensagem}</p>
    </div>
    <Button
      variant="outline"
      size="sm"
      className="shrink-0"
      onClick={() => onToggleLida(mensagem.id, mensagem.lida)}
      disabled={saving}
    >
      {mensagem.lida ? <MailOpen size={14} /> : <Mail size={14} />}
      {mensagem.lida ? "Marcar não lida" : "Marcar lida"}
    </Button>
  </div>
);

const SuporteTab = () => {
  const [mensagens, setMensagens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState("todas");
  const [savingId, setSavingId] = useState(null);

  const fetchMensagens = async () => {
    setLoading(true);
    setErro(null);
    try {
      const { data, error } = await supabase
        .from("mensagens_suporte")
        .select("*, familia:usuarios!familia_id(nome, sobrenome, email)")
        .order("criado_em", { ascending: false });
      if (error) throw error;
      setMensagens(data || []);
    } catch (e) {
      console.error("Erro ao buscar mensagens de suporte:", e);
      setErro(e.message || "Falha ao carregar mensagens.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMensagens();
  }, []);

  const toggleLida = async (id, lida) => {
    setSavingId(id);
    setMensagens((prev) => prev.map((m) => (m.id === id ? { ...m, lida: !lida } : m)));
    const { error } = await supabase
      .from("mensagens_suporte")
      .update({ lida: !lida })
      .eq("id", id);
    if (error) {
      console.error("Erro ao atualizar mensagem:", error);
      setMensagens((prev) => prev.map((m) => (m.id === id ? { ...m, lida } : m)));
      alert("Não foi possível atualizar a mensagem.");
    }
    setSavingId(null);
  };

  const naoLidas = useMemo(() => mensagens.filter((m) => !m.lida).length, [mensagens]);

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return mensagens.filter((m) => {
      const casaFiltro =
        filtro === "todas" || (filtro === "nao_lidas" ? !m.lida : m.lida);
      if (!termo) return casaFiltro;
      const alvo = `${nomeCompleto(m.familia)} ${m.familia?.email ?? ""} ${m.assunto ?? ""} ${m.mensagem ?? ""}`.toLowerCase();
      return casaFiltro && alvo.includes(termo);
    });
  }, [mensagens, busca, filtro]);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard titulo="Não lidas" valor={naoLidas} icone={<Mail size={22} />} className="bg-indigo-50 border-indigo-100" />
        <MetricCard titulo="Total" valor={mensagens.length} icone={<Inbox size={22} />} className="bg-slate-50 border-slate-200" />
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="admin-suporte-busca"
            placeholder="Buscar por família, assunto ou texto..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filtro} onValueChange={setFiltro}>
          <SelectTrigger className="sm:w-48" id="admin-suporte-filtro">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas</SelectItem>
            <SelectItem value="nao_lidas">Não lidas</SelectItem>
            <SelectItem value="lidas">Lidas</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchMensagens} disabled={loading}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Recarregar
        </Button>
      </div>

      {erro && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-4 text-sm text-red-700">
            <p className="font-semibold">Não foi possível carregar as mensagens.</p>
            <p className="text-xs mt-1">{erro}</p>
          </CardContent>
        </Card>
      )}

      {loading && !erro && (
        <Card className="border-dashed">
          <CardContent className="py-6 text-sm text-muted-foreground">Carregando mensagens...</CardContent>
        </Card>
      )}

      {!loading && !erro && filtradas.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-6 text-sm text-muted-foreground">
            {mensagens.length === 0
              ? "Nenhuma mensagem de suporte até agora."
              : "Nenhuma mensagem corresponde ao filtro."}
          </CardContent>
        </Card>
      )}

      {!loading && !erro && filtradas.length > 0 && (
        <div className="space-y-2">
          {filtradas.map((m) => (
            <MensagemRow key={m.id} mensagem={m} onToggleLida={toggleLida} saving={savingId === m.id} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Gestão interna do fluxo família: solicitações de contratação (a família pede,
 * a equipe intermedia) e mensagens do canal de suporte. RLS: policies de admin
 * em solicitacoes_familia e mensagens_suporte permitem leitura/edição total.
 */
const SolicitacoesAdminContent = () => {
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-foreground">Solicitações & Suporte</h1>
        <p className="text-sm text-muted-foreground">
          Contratações pedidas pelas famílias e mensagens do canal de suporte. A
          equipe intermedia todo o contato com as babás.
        </p>
      </div>

      <Tabs defaultValue="contratacoes">
        <TabsList>
          <TabsTrigger value="contratacoes">Contratações</TabsTrigger>
          <TabsTrigger value="suporte">Suporte</TabsTrigger>
        </TabsList>
        <TabsContent value="contratacoes" className="mt-4">
          <ContratacoesTab />
        </TabsContent>
        <TabsContent value="suporte" className="mt-4">
          <SuporteTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SolicitacoesAdminContent;
